/**
 * Responsibility: what is actually for sale right now, cheapest first.
 *
 * The rest of the dialog answers "what is this card worth". This answers the
 * question that follows it: what can I buy today, for how much, and does it
 * look gradeable. That last part is why every row carries its picture - on a
 * card whose whole thesis is gem rate, a median cannot tell you the cheap copy
 * is off-centre, and the photograph can.
 *
 * Ordering is by total, not by the item price, and the postage is shown
 * beside it so the ordering is checkable. A dollar card with twelve dollars
 * of postage is not the cheapest thing on the page, and eBay's own price sort
 * says it is.
 *
 * Raw and slabbed are split on what each listing **is**, server-side, not on
 * which search found it. eBay's relevance is per query, so two searches are
 * run - but a query for "<card> PSA" returns raw cards marketed to graders
 * ("PSA ready", "would grade a 10"), and those were landing under Slabs.
 *
 * The grade filter reads the grader and the grade off the title. A slab whose
 * title names no number is still a slab and sits under "All"; putting it
 * under the PSA 10 filter would be the one mistake here that costs money.
 */
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import GavelIcon from '@mui/icons-material/Gavel';
import LaunchIcon from '@mui/icons-material/Launch';
import type { Listing } from '../../api/client';
import { CardThumb } from './CardThumb';
import { money } from './money';

const THUMB_WIDTH = 52;

/** Tall enough to show several rows, short enough to keep the dialog square. */
const LIST_HEIGHT = 320;

type GradeFilter = 'all' | '10' | '9';

function postage(listing: Listing): string {
  if (listing.shipping === null || listing.shipping === undefined) return 'postage unknown';
  return Number(listing.shipping) === 0 ? 'free postage' : `+ ${money(listing.shipping)} postage`;
}

function ListingRow({ listing }: { listing: Listing }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        py: 1,
        px: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minWidth: 0,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <CardThumb src={listing.image_url} alt={listing.title} width={THUMB_WIDTH} />

      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Link
          href={listing.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          underline="hover"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {listing.title}
          <LaunchIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
        </Link>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">
            {postage(listing)}
          </Typography>
          {listing.is_graded ? (
            <Chip
              size="small"
              variant="outlined"
              label={
                listing.grade_label
                  ? `${listing.grading_company ?? 'Graded'} ${listing.grade_label}`
                  : `${listing.grading_company ?? 'Graded'} slab`
              }
            />
          ) : null}
          {listing.is_auction ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              icon={<GavelIcon sx={{ fontSize: 13 }} />}
              label="bid"
            />
          ) : null}
        </Stack>
      </Stack>

      <Stack spacing={0} alignItems="flex-end" sx={{ flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
        >
          {money(listing.total)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {listing.is_auction ? 'current bid' : 'total'}
        </Typography>
      </Stack>
    </Stack>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', height: LIST_HEIGHT, px: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        {message}
      </Typography>
    </Box>
  );
}

type ListingsListProps = {
  listings: Listing[] | undefined;
  loading: boolean;
  /** Offers the PSA 9 / 10 / all filter. Only meaningful for slabs. */
  gradeFilter?: boolean;
  emptyMessage: string;
};

export function ListingsList({
  listings,
  loading,
  gradeFilter = false,
  emptyMessage,
}: ListingsListProps) {
  const [grade, setGrade] = useState<GradeFilter>('all');

  const shown = useMemo(() => {
    const rows = listings ?? [];
    if (!gradeFilter || grade === 'all') return rows;
    return rows.filter((row) => String(row.grade) === grade);
  }, [listings, gradeFilter, grade]);

  const counts = useMemo(() => {
    const rows = listings ?? [];
    return {
      all: rows.length,
      ten: rows.filter((row) => row.grade === 10).length,
      nine: rows.filter((row) => row.grade === 9).length,
    };
  }, [listings]);

  const companies = useMemo(() => {
    const names = new Set(
      (listings ?? [])
        .map((row) => row.grading_company)
        .filter((name): name is string => Boolean(name)),
    );
    return [...names].sort();
  }, [listings]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: LIST_HEIGHT }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {gradeFilter ? (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grade}
          onChange={(_event, next: GradeFilter | null) => next && setGrade(next)}
          sx={{ alignSelf: 'flex-start' }}
        >
          <ToggleButton value="all">All ({counts.all})</ToggleButton>
          <ToggleButton value="10">Grade 10 ({counts.ten})</ToggleButton>
          <ToggleButton value="9">Grade 9 ({counts.nine})</ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      {/* Which graders are actually represented. Worth saying, because a
          BGS 9.5 and a PSA 9 are not the same thing at the same price. */}
      {gradeFilter && companies.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          Graded by {companies.join(', ')}
        </Typography>
      ) : null}

      {shown.length === 0 ? (
        <Empty
          message={
            gradeFilter && grade !== 'all' && counts.all > 0
              ? `No grade ${grade} copies listed right now. ${counts.all} other slabs are.`
              : emptyMessage
          }
        />
      ) : (
        <Box sx={{ maxHeight: LIST_HEIGHT, overflowY: 'auto', pr: 0.5 }}>
          {shown.map((listing) => (
            <ListingRow key={listing.item_id} listing={listing} />
          ))}
        </Box>
      )}
    </Stack>
  );
}
